import { randomBytes } from 'node:crypto'
import type { FastifyInstance } from 'fastify'
import type { IdentityServiceImpl } from '../identity-service.js'

interface SetupDeps {
  service: IdentityServiceImpl
  tokenStore:
    | {
        getUserId(tokenId: string): string | undefined
        setUserId(tokenId: string, userId: string): void
        getByIdentitySecret(secret: string): { id: string } | undefined
      }
    | undefined
}

/**
 * Short-lived in-memory link codes for multi-device account linking.
 *
 * These are intentionally not persisted: a restart invalidates all pending codes,
 * which is acceptable given the 5-minute TTL. Persistence would add complexity
 * (storage, migration) for minimal practical benefit.
 */
const linkCodes = new Map<string, { userId: string; expiresAt: number }>()

/**
 * Registers identity setup routes under the plugin's prefix (e.g. /api/v1/identity).
 *
 * POST /setup — first-time identity claim for API token holders.
 * POST /link-code — generate a one-time code to link a second device/token.
 *
 * Five paths for POST /setup (checked in priority order):
 * 1. Token already linked → idempotent return of existing user.
 * 2. identitySecret → re-link new token to existing user (reconnect flow).
 * 3. linkCode → link to existing user via short-lived link code (multi-device).
 * 4. displayName → create new user (first-time setup).
 * 5. else → 400 Bad Request.
 */
export function registerSetupRoutes(app: FastifyInstance, deps: SetupDeps): void {
  const { service, tokenStore } = deps

  app.post('/setup', async (request, reply) => {
    const auth = (request as any).auth
    if (!auth?.tokenId) return reply.status(401).send({ error: 'JWT required' })

    // Path 1: idempotent — token already linked, return existing user
    const existingUserId = tokenStore?.getUserId?.(auth.tokenId)
    if (existingUserId) {
      const user = await service.getUser(existingUserId)
      if (user) return user
    }

    const body = request.body as any

    // Path 2: re-link via identitySecret — silent reconnect flow.
    // The App holds the old token's identitySecret and sends it to link the
    // new token to the same user without requiring manual re-setup.
    if (body?.identitySecret) {
      const oldToken = tokenStore?.getByIdentitySecret(body.identitySecret as string)
      if (!oldToken) {
        return reply.status(401).send({ error: 'Invalid identity secret' })
      }
      const userId = tokenStore?.getUserId(oldToken.id)
      if (!userId) {
        return reply.status(401).send({ error: 'No identity linked to this secret' })
      }
      await service.createIdentity(userId, {
        source: 'api',
        platformId: auth.tokenId as string,
      })
      tokenStore?.setUserId?.(auth.tokenId as string, userId)
      return service.getUser(userId)
    }

    // Path 3: link-code — multi-device linking.
    // The code was generated by the existing token's /link-code endpoint.
    if (body?.linkCode) {
      const entry = linkCodes.get(body.linkCode as string)
      if (!entry || entry.expiresAt < Date.now()) {
        return reply.status(401).send({ error: 'Invalid or expired link code' })
      }
      linkCodes.delete(body.linkCode as string)

      await service.createIdentity(entry.userId, {
        source: 'api',
        platformId: auth.tokenId as string,
      })

      tokenStore?.setUserId?.(auth.tokenId as string, entry.userId)
      return service.getUser(entry.userId)
    }

    // Path 4: new user — displayName required, username optional
    if (!body?.displayName) return reply.status(400).send({ error: 'displayName is required' })

    // Validate username format — same rules as /whoami command
    const USERNAME_RE = /^[a-zA-Z0-9_.-]+$/
    if (body.username !== undefined && body.username !== null) {
      const u = String(body.username)
      if (!u || !USERNAME_RE.test(u)) {
        return reply.status(400).send({ error: 'Invalid username. Only letters, numbers, _ . - allowed.' })
      }
    }

    // Check username uniqueness before creating
    if (body.username) {
      const existing = await service.getUserByUsername(String(body.username))
      if (existing) {
        return reply.status(409).send({ error: 'Username already taken' })
      }
    }

    const { user } = await service.createUserWithIdentity({
      displayName: body.displayName as string,
      username: body.username as string | undefined,
      source: 'api',
      platformId: auth.tokenId as string,
    })

    tokenStore?.setUserId?.(auth.tokenId as string, user.userId)
    return user
  })

  // POST /link-code — generate a one-time code so another token can link to this user.
  // The caller must already have a linked identity. Code expires in 5 minutes.
  app.post('/link-code', async (request, reply) => {
    const auth = (request as any).auth
    if (!auth?.tokenId) return reply.status(401).send({ error: 'JWT required' })

    const userId = tokenStore?.getUserId?.(auth.tokenId as string)
    if (!userId) return reply.status(403).send({ error: 'Identity not set up' })

    const code = randomBytes(16).toString('hex')
    const expiresAt = Date.now() + 5 * 60 * 1000

    // Evict stale codes before inserting to keep the map bounded
    for (const [k, v] of linkCodes) {
      if (v.expiresAt < Date.now()) linkCodes.delete(k)
    }

    linkCodes.set(code, { userId, expiresAt })
    return { linkCode: code, expiresAt: new Date(expiresAt).toISOString() }
  })
}
